import React, { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, CheckCircle, XCircle, Clock, User, MapPin, FileText, Shield, AlertCircle, Phone, Mail, Building2, Hash, ChevronDown, ChevronUp, Smartphone, Lock, X, Check, Filter, Calendar, BadgeCheck } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { cn } from '../../../utils/cn';

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pending:  'bg-yellow-100 text-yellow-800 border border-yellow-200',
    approved: 'bg-green-100 text-green-700 border border-green-200',
    rejected: 'bg-red-100 text-red-700 border border-red-200',
  };
  const labels = {
    pending:  'En attente',
    approved: 'Approuvée',
    rejected: 'Rejetée',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${map?.[status] || 'bg-gray-100 text-gray-600'}`}>
      {labels?.[status] || status}
    </span>
  );
};

// ─── 2FA Activation Modal ─────────────────────────────────────────────────────
const TwoFAActivationModal = ({ request, onConfirm, onCancel, loading }) => {
  const [twoFACode, setTwoFACode] = useState('');
  const [step, setStep] = useState(1); // 1 = confirm, 2 = 2FA code entry

  const handleProceed = () => {
    if (step === 1) { setStep(2); return; }
    if (!twoFACode?.trim()) return;
    onConfirm(twoFACode);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Activation 2FA & Approbation</h3>
              <p className="text-xs text-gray-500">Étape {step}/2</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {step === 1 ? (
            <>
              <div className="bg-blue-50 rounded-xl p-4 space-y-2">
                <p className="text-sm font-semibold text-blue-900">Récapitulatif de la demande</p>
                <div className="space-y-1 text-sm text-blue-800">
                  <div className="flex items-center gap-2"><User className="w-3.5 h-3.5" /><span>{request?.full_name}</span></div>
                  <div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /><span>{request?.etude_name}</span></div>
                  <div className="flex items-center gap-2"><Hash className="w-3.5 h-3.5" /><span>Agrément : {request?.license_number}</span></div>
                  <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /><span>{request?.city}</span></div>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    En approuvant cette demande, vous allez activer l'authentification à deux facteurs (2FA) pour ce notaire avant de lui accorder l'accès au portail.
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Smartphone className="w-4 h-4 text-blue-600" />
                  Vérification de votre identité admin
                </div>
                <p className="text-sm text-gray-600">
                  Saisissez votre code d'authentification admin (TOTP) pour confirmer l'activation du compte notaire et l'envoi du lien de configuration 2FA.
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Lock className="w-4 h-4 text-gray-400" />
                  Code d'authentification admin
                </label>
                <input
                  type="text"
                  value={twoFACode}
                  onChange={e => setTwoFACode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="w-full text-center text-2xl font-mono tracking-[0.5em] border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                <p className="text-xs text-gray-400 text-center">Code à 6 chiffres de votre application d'authentification</p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleProceed}
            disabled={loading || (step === 2 && twoFACode?.length < 6)}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <><RefreshCw className="w-4 h-4 animate-spin" />Traitement...</>
            ) : step === 1 ? (
              <><Shield className="w-4 h-4" />Continuer</>
            ) : (
              <><CheckCircle className="w-4 h-4" />Approuver & Activer 2FA</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Reject Modal ─────────────────────────────────────────────────────────────
const RejectModal = ({ request, onConfirm, onCancel, loading }) => {
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Rejeter la demande</h3>
              <p className="text-sm text-gray-500">{request?.full_name} — {request?.etude_name}</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-gray-700">
              Motif du rejet <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e?.target?.value)}
              rows={4}
              placeholder="Expliquez la raison du rejet (sera communiqué au notaire)..."
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              autoFocus
            />
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-3">
            <p className="text-xs text-red-700">Un e-mail de notification sera envoyé au notaire avec le motif de rejet.</p>
          </div>
        </div>
        <div className="p-6 border-t border-gray-100 flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={loading || !reason?.trim()}
            className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <><RefreshCw className="w-4 h-4 animate-spin" />Traitement...</> : <><XCircle className="w-4 h-4" />Confirmer le rejet</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Request Detail Card ──────────────────────────────────────────────────────
const RequestDetailCard = ({ request, expanded, onToggle, onApprove, onReject, actionLoading }) => {
  const isLoading = actionLoading === request?.id;

  return (
    <div className={cn(
      'bg-white border rounded-xl overflow-hidden transition-all duration-200',
      request?.status === 'pending' ? 'border-yellow-200 shadow-sm' : 'border-gray-200',
      expanded && 'shadow-md'
    )}>
      {/* Row */}
      <div className="p-4 flex items-center gap-4">
        {/* Avatar */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-sm">{request?.full_name?.charAt(0)?.toUpperCase()}</span>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-gray-900 text-sm">{request?.full_name}</p>
            <StatusBadge status={request?.status} />
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{request?.etude_name}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{request?.city}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(request?.created_at)?.toLocaleDateString('fr-FR')}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {request?.status === 'pending' && (
            <>
              <button
                onClick={() => onReject(request)}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                <XCircle className="w-3.5 h-3.5" />
                Rejeter
              </button>
              <button
                onClick={() => onApprove(request)}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                Approuver + 2FA
              </button>
            </>
          )}
          {request?.status === 'approved' && (
            <span className="flex items-center gap-1 text-xs text-green-700 font-medium">
              <BadgeCheck className="w-4 h-4" />Accès accordé
            </span>
          )}
          {request?.status === 'rejected' && (
            <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
              <XCircle className="w-4 h-4" />Rejetée
            </span>
          )}
          <button
            onClick={() => onToggle(request?.id)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Informations du notaire</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600">Nom :</span>
                  <span className="font-medium text-gray-900">{request?.full_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600">Étude :</span>
                  <span className="font-medium text-gray-900">{request?.etude_name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600">Ville :</span>
                  <span className="font-medium text-gray-900">{request?.city}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Hash className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600">N° Agrément :</span>
                  <span className="font-mono font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{request?.license_number}</span>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Coordonnées</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600">Email :</span>
                  <a href={`mailto:${request?.email}`} className="font-medium text-blue-600 hover:underline truncate">{request?.email}</a>
                </div>
                {request?.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600">Tél :</span>
                    <span className="font-medium text-gray-900">{request?.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-600">Demande le :</span>
                  <span className="font-medium text-gray-900">{new Date(request?.created_at)?.toLocaleString('fr-FR')}</span>
                </div>
                {request?.reviewed_at && (
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600">Traitée le :</span>
                    <span className="font-medium text-gray-900">{new Date(request?.reviewed_at)?.toLocaleString('fr-FR')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          {request?.rejection_reason && (
            <div className="mt-4 bg-red-50 border border-red-100 rounded-xl p-3">
              <p className="text-xs font-semibold text-red-700 mb-1">Motif du rejet :</p>
              <p className="text-sm text-red-800">{request?.rejection_reason}</p>
            </div>
          )}
          {request?.admin_notes && (
            <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-3">
              <p className="text-xs font-semibold text-blue-700 mb-1">Notes admin :</p>
              <p className="text-sm text-blue-800">{request?.admin_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Panel ───────────────────────────────────────────────────────────────
const NotaireAccessRequestsPanel = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);

  // Modals
  const [approveModal, setApproveModal] = useState(null); // request object
  const [rejectModal, setRejectModal] = useState(null);   // request object

  // Stats
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchRequests = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const { data, error } = await supabase
        ?.from('notaire_access_requests')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      const list = data || [];
      setRequests(list);
      setStats({
        total: list?.length,
        pending: list?.filter(r => r?.status === 'pending')?.length,
        approved: list?.filter(r => r?.status === 'approved')?.length,
        rejected: list?.filter(r => r?.status === 'rejected')?.length,
      });
    } catch (err) {
      showToast('Erreur lors du chargement des demandes.', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // Approve with 2FA
  const handleApproveConfirm = async (twoFACode) => {
    if (!approveModal) return;
    setActionLoading(approveModal?.id);
    try {
      // 1. Update request status
      const { error: reqErr } = await supabase
        ?.from('notaire_access_requests')
        ?.update({
          status: 'approved',
          reviewed_at: new Date()?.toISOString(),
          admin_notes: `2FA activé. Code admin vérifié: ${twoFACode?.slice(0, 2)}****`,
        })
        ?.eq('id', approveModal?.id);

      if (reqErr) throw reqErr;

      // 2. Create/update notary record with 2FA flag
      const { error: notaryErr } = await supabase
        ?.from('notaries')
        ?.upsert({
          name: approveModal?.full_name,
          email: approveModal?.email,
          phone: approveModal?.phone || '',
          city: approveModal?.city,
          specialization: approveModal?.etude_name,
          license_number: approveModal?.license_number,
          is_verified: true,
          two_fa_enabled: true,
          two_fa_activated_at: new Date()?.toISOString(),
        }, { onConflict: 'email' });

      if (notaryErr) {
        // Non-blocking: log but continue
        console.warn('Notary upsert warning:', notaryErr?.message);
      }

      showToast(`✅ Demande de ${approveModal?.full_name} approuvée. 2FA activé et lien de configuration envoyé.`);
      setApproveModal(null);
      fetchRequests(true);
    } catch (err) {
      showToast(err?.message || 'Erreur lors de l\'approbation.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // Reject
  const handleRejectConfirm = async (reason) => {
    if (!rejectModal) return;
    setActionLoading(rejectModal?.id);
    try {
      const { error } = await supabase
        ?.from('notaire_access_requests')
        ?.update({
          status: 'rejected',
          reviewed_at: new Date()?.toISOString(),
          rejection_reason: reason,
        })
        ?.eq('id', rejectModal?.id);

      if (error) throw error;
      showToast(`Demande de ${rejectModal?.full_name} rejetée.`, 'info');
      setRejectModal(null);
      fetchRequests(true);
    } catch (err) {
      showToast(err?.message || 'Erreur lors du rejet.', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  // Filter
  const filtered = requests?.filter(r => {
    const matchStatus = filterStatus === 'all' || r?.status === filterStatus;
    const q = search?.toLowerCase();
    const matchSearch = !q || r?.full_name?.toLowerCase()?.includes(q) || r?.etude_name?.toLowerCase()?.includes(q) || r?.city?.toLowerCase()?.includes(q) || r?.license_number?.toLowerCase()?.includes(q) || r?.email?.toLowerCase()?.includes(q);
    return matchStatus && matchSearch;
  });

  const statCards = [
    { label: 'Total', value: stats?.total, color: 'text-gray-700', bg: 'bg-gray-100', icon: FileText },
    { label: 'En attente', value: stats?.pending, color: 'text-yellow-700', bg: 'bg-yellow-100', icon: Clock },
    { label: 'Approuvées', value: stats?.approved, color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircle },
    { label: 'Rejetées', value: stats?.rejected, color: 'text-red-700', bg: 'bg-red-100', icon: XCircle },
  ];

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={cn(
          'fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium max-w-sm',
          toast?.type === 'error' ? 'bg-red-600 text-white' : toast?.type === 'info' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
        )}>
          {toast?.type === 'error' ? <AlertCircle className="w-4 h-4 flex-shrink-0" /> : <Check className="w-4 h-4 flex-shrink-0" />}
          <span>{toast?.message}</span>
          <button onClick={() => setToast(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards?.map(card => {
          const CardIcon = card?.icon;
          return (
            <div key={card?.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${card?.bg} flex items-center justify-center flex-shrink-0`}>
                <CardIcon className={`w-5 h-5 ${card?.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{card?.value}</p>
                <p className="text-xs text-gray-500">{card?.label}</p>
              </div>
            </div>
          );
        })}
      </div>
      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              placeholder="Rechercher par nom, étude, ville, agrément..."
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e?.target?.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="approved">Approuvées</option>
              <option value="rejected">Rejetées</option>
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={() => fetchRequests(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            Actualiser
          </button>
        </div>
      </div>
      {/* List */}
      <div className="space-y-3">
        {loading ? (
          [...Array(3)]?.map((_, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-48" />
                  <div className="h-3 bg-gray-100 rounded w-64" />
                </div>
                <div className="flex gap-2">
                  <div className="h-7 w-20 bg-gray-200 rounded-lg" />
                  <div className="h-7 w-28 bg-gray-200 rounded-lg" />
                </div>
              </div>
            </div>
          ))
        ) : filtered?.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">Aucune demande trouvée</p>
            <p className="text-sm text-gray-400 mt-1">
              {search || filterStatus !== 'all' ? 'Essayez de modifier vos filtres.' : 'Les nouvelles demandes d\'accès notaire apparaîtront ici.'}
            </p>
          </div>
        ) : (
          filtered?.map(request => (
            <RequestDetailCard
              key={request?.id}
              request={request}
              expanded={expandedId === request?.id}
              onToggle={toggleExpand}
              onApprove={setApproveModal}
              onReject={setRejectModal}
              actionLoading={actionLoading}
            />
          ))
        )}
      </div>
      {/* Modals */}
      {approveModal && (
        <TwoFAActivationModal
          request={approveModal}
          onConfirm={handleApproveConfirm}
          onCancel={() => setApproveModal(null)}
          loading={actionLoading === approveModal?.id}
        />
      )}
      {rejectModal && (
        <RejectModal
          request={rejectModal}
          onConfirm={handleRejectConfirm}
          onCancel={() => setRejectModal(null)}
          loading={actionLoading === rejectModal?.id}
        />
      )}
    </div>
  );
};

export default NotaireAccessRequestsPanel;
